<template>
  <section v-if="show" class="search-panel" @click.stop>
    <div class="search-box">
      <i class="fa fa-search" aria-hidden="true"></i>
      <input
        ref="inputEl"
        type="text"
        placeholder="Search local tracks"
        @input="$emit('search', $event.target.value)"
      />
      <button type="button" @click="$emit('close')">
        <i class="fa fa-times" aria-hidden="true"></i>
        <span class="sr-only">Close search</span>
      </button>
    </div>

    <ul v-if="music.searchItems.length" ref="resultEl" class="search-results">
      <li
        v-for="(item, index) in music.searchItems"
        :key="item.name || index"
        class="search-row"
        :class="{
          selected: music.searchCursorIndex === index,
          current: currentItem === item,
        }"
      >
        <button class="queue-button" type="button" @click="toggleQueue(item.index)">
          <i
            class="fa"
            :class="playQueueSet.has(item.index) ? 'fa-minus' : 'fa-plus'"
            aria-hidden="true"
          ></i>
        </button>
        <button class="search-title" type="button" @click="selectItem(item.index)">
          {{ formatTrackName(item.name) }}
        </button>
      </li>
    </ul>

    <p v-else class="search-empty">Type to filter your local library.</p>
  </section>
</template>

<script setup>
/* global defineProps, defineEmits, defineExpose */
import { nextTick, ref, watch } from 'vue'

const SEARCH_ITEM_HEIGHT = 40

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  music: {
    type: Object,
    required: true,
  },
  currentItem: {
    type: Object,
    default: null,
  },
  playQueueSet: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['close', 'search', 'set-current', 'push-queue', 'pop-queue'])

const inputEl = ref(null)
const resultEl = ref(null)

watch(
  () => props.show,
  async (show) => {
    if (!show) return
    await nextTick()
    inputEl.value?.focus()
  }
)

function focusInput() {
  inputEl.value?.focus()
}

function blurInput() {
  inputEl.value?.blur()
}

function isInputFocused() {
  return document.activeElement === inputEl.value
}

function scrollToIndex(index) {
  resultEl.value?.scrollTo({
    top: index * SEARCH_ITEM_HEIGHT - resultEl.value.clientHeight / 2,
  })
}

function formatTrackName(name = '') {
  return name.replace(/\.(mp3|wav|ogg|webm)$/i, '')
}

function toggleQueue(index) {
  emit(props.playQueueSet.has(index) ? 'pop-queue' : 'push-queue', index)
}

function selectItem(index) {
  emit('set-current', index)
  emit('close')
}

defineExpose({
  focusInput,
  blurInput,
  isInputFocused,
  scrollToIndex,
})
</script>

<style scoped lang="less">
.search-panel {
  position: absolute;
  top: 26px;
  right: 28px;
  left: 28px;
  z-index: 5;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  background: rgba(10, 10, 10, 0.94);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.48);
  backdrop-filter: blur(20px);
}

.search-box {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 36px;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.search-box i {
  color: #a29b98;
  text-align: center;
}

.search-box input {
  width: 100%;
  height: 48px;
  margin: 0;
  border: 0;
  color: #f7f2ee;
  background: transparent;
  outline: none;
}

.search-box button,
.queue-button,
.search-title {
  margin: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.search-box button {
  height: 36px;
  padding: 0;
  color: #a29b98;
}

.search-results {
  max-height: 280px;
  overflow: auto;
  padding: 8px;
}

.search-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  min-height: 40px;
  align-items: center;
  border-radius: 8px;
  color: #c8c1be;
}

.search-row.selected,
.search-row:hover {
  background: rgba(255, 255, 255, 0.075);
}

.search-row.current {
  color: #ffffff;
}

.queue-button {
  width: 30px;
  height: 30px;
  padding: 0;
}

.search-title {
  overflow: hidden;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-align: left;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.search-empty {
  padding: 18px 20px 22px;
  color: #8f8987;
  font-size: 13px;
}
</style>
